import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ErrorMessage } from "./ErrorMessage";
import { SuccessMessage } from "./SuccessMessage";

export function RegisterForm() {
  const [formData, setFormData] = useState({
    first_name: "",
    surname: "",
    known_as: "",
    title: "",
    physical_address: "",
    cell_number: "",
    home_number: "",
    preferred_number: "cell",
    email_address: "",
    year_started: new Date().getFullYear(),
    password: "",
    confirm_password: ""
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setShowError(false);
    setShowSuccess(false);

    // Validate passwords match
    if (formData.password !== formData.confirm_password) {
      setShowError(true);
      setErrorMessage("Passwords do not match");
      setIsLoading(false);
      // Scroll to top to show error message
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // Validate password strength
    if (formData.password.length < 6) {
      setShowError(true);
      setErrorMessage("Password must be at least 6 characters long");
      setIsLoading(false);
      // Scroll to top to show error message
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    try {
      const registrationData = {
        first_name: formData.first_name,
        surname: formData.surname,
        known_as: formData.known_as,
        title: formData.title,
        physical_address: formData.physical_address,
        cell_number: formData.cell_number,
        home_number: formData.home_number,
        preferred_number: formData.preferred_number,
        email_address: formData.email_address.toLowerCase(),
        year_started: parseInt(formData.year_started),
        password: formData.password,
        confirm_password: formData.confirm_password
      };

      const res = await axios.post("/api/user/register", registrationData);

      setShowSuccess(true);
      setSuccessMessage(res.data.message);
      
      // Clear form on success
      setFormData({
        first_name: "",
        surname: "",
        known_as: "",
        title: "",
        physical_address: "",
        cell_number: "",
        home_number: "",
        preferred_number: "cell",
        email_address: "",
        year_started: new Date().getFullYear(),
        password: "",
        confirm_password: ""
      });

      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: 'smooth' });

    } catch (error) {
      setShowError(true);
      if (error.response?.data?.detail) {
        setErrorMessage(error.response.data.detail);
      } else {
        setErrorMessage("Registration failed. Please try again.");
      }
      // Scroll to top to show error message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{ backgroundColor: "#F0F0F1", minHeight: "100vh", paddingTop: "0px", fontFamily: "'Montserrat', sans-serif" }}>
      <div className="container py-5">
        <div className="d-flex flex-column align-items-center">
          <div className="text-center mb-4">
            <h1 className="h2">Member Registration</h1>
          </div>

          {showError && (
            <ErrorMessage 
              title="Registration Failed" 
              message={errorMessage}
              width="550px"
            />
          )}

          {showSuccess && (
            <SuccessMessage 
              title="Registration Successful" 
              message={successMessage}
              width="550px"
            />
          )}

          <div className="card shadow-sm rounded-0 bg-white mt-3" style={{ padding: "30px", width: "550px" }}>
            <form onSubmit={handleSubmit}>
              {/* Personal Information */}
              <h5 className="mb-3 primary-color">Personal Information</h5>
              
              <div className="row mb-3">
                <div className="col-md-6">
                  <label htmlFor="first_name" className="form-label">First Name *</label>
                  <input
                    type="text"
                    id="first_name"
                    name="first_name"
                    className="form-control"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label htmlFor="surname" className="form-label">Surname *</label>
                  <input
                    type="text"
                    id="surname"
                    name="surname"
                    className="form-control"
                    value={formData.surname}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-6">
                  <label htmlFor="known_as" className="form-label">Known As</label>
                  <input
                    type="text"
                    id="known_as"
                    name="known_as"
                    className="form-control"
                    value={formData.known_as}
                    onChange={handleInputChange}
                    placeholder="Nickname or preferred name"
                  />
                </div>
                <div className="col-md-6">
                  <label htmlFor="title" className="form-label">Title</label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    className="form-control"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="Dr., Mr., Ms., etc."
                  />
                </div>
              </div>

              {/* Contact Information */}
              <h5 className="mb-3 primary-color mt-4">Contact Information</h5>

              <div className="mb-3">
                <label htmlFor="physical_address" className="form-label">Physical Address</label>
                <textarea
                  id="physical_address"
                  name="physical_address"
                  className="form-control"
                  rows="3"
                  value={formData.physical_address}
                  onChange={handleInputChange}
                />
              </div>

              <div className="row mb-3">
                <div className="col-md-6">
                  <label htmlFor="cell_number" className="form-label">Cell Number</label>
                  <input
                    type="tel"
                    id="cell_number"
                    name="cell_number"
                    className="form-control"
                    value={formData.cell_number}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="col-md-6">
                  <label htmlFor="home_number" className="form-label">Home Number</label>
                  <input
                    type="tel"
                    id="home_number"
                    name="home_number"
                    className="form-control"
                    value={formData.home_number}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="mb-3">
                <label htmlFor="preferred_number" className="form-label">Preferred Number</label>
                <select
                  id="preferred_number"
                  name="preferred_number"
                  className="form-select"
                  value={formData.preferred_number}
                  onChange={handleInputChange}
                >
                  <option value="cell">Cell</option>
                  <option value="home">Home</option>
                </select>
              </div>

              <div className="mb-3">
                <label htmlFor="email_address" className="form-label">Email Address *</label>
                <input
                  type="email"
                  id="email_address"
                  name="email_address"
                  className="form-control"
                  value={formData.email_address}
                  onChange={handleInputChange}
                  required
                  autoComplete="email"
                />
              </div>

              {/* Membership Information */}
              <h5 className="mb-3 primary-color mt-4">Membership Information</h5>

              <div className="mb-3">
                <label htmlFor="year_started" className="form-label">Year Started *</label>
                <input
                  type="number"
                  id="year_started"
                  name="year_started"
                  className="form-control"
                  value={formData.year_started}
                  onChange={handleInputChange}
                  required
                  min="1900"
                  max={new Date().getFullYear()}
                />
              </div>

              {/* Account Security */}
              <h5 className="mb-3 primary-color mt-4">Account Security</h5>

              <div className="mb-3">
                <label htmlFor="password" className="form-label">Password *</label>
                <div className="input-group">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    className="form-control"
                    value={formData.password}
                    onChange={handleInputChange}
                    required
                    autoComplete="new-password"
                    minLength="6"
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex="-1"
                  >
                    {showPassword ? "Hide" : "Show"}
                  </button>
                </div>
                <div className="form-text">Password must be at least 6 characters long.</div>
              </div>

              <div className="mb-4">
                <label htmlFor="confirm_password" className="form-label">Confirm Password *</label>
                <input
                  type={showPassword ? "text" : "password"}
                  id="confirm_password"
                  name="confirm_password"
                  className="form-control"
                  value={formData.confirm_password}
                  onChange={handleInputChange}
                  required
                  autoComplete="new-password"
                />
              </div>

              <button
                type="submit"
                className="btn btn-admin w-100"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Registering...
                  </>
                ) : (
                  "Register"
                )}
              </button>
            </form>

            <div className="mt-3 text-center">
              <hr />
              <p className="mb-2">Already have an account?</p>
              <Link to="/members" className="btn btn-custom-outline">
                Log In
              </Link>
            </div>
          </div>
          <Link className="mt-3 small-link" to="/">← Return to Website</Link>
        </div>
      </div>
    </div>
  );
}
